import {
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  updateUserAttributes,
  getCurrentUser,
  signIn,
  confirmSignIn
} from 'aws-amplify/auth';

import { AuthError, UserAttributes } from '../../core';

// Custom error message overrides
const errorMessageMap = new Map<string, string>();
errorMessageMap.set('Username cannot be empty', 'Email cannot be empty.');
errorMessageMap.set('Custom auth lambda trigger is not configured for the user pool.', 'Password cannot be empty.');

const getErrorMessage = (message: string): string => {
  return errorMessageMap.get(message) ?? message;
};

const convertError = (error: any): AuthError => {
  console.debug(error.message);
  return {
    error: {
      code: error.code,
      message: getErrorMessage(error.message)
    }
  };
};

export const getCurrentUserData = async (): Promise<any | undefined> => {
  try {
    const user = await getCurrentUser();
    return user;
  } catch {
    return undefined;
  }
};

// export const getUserId = async (): Promise<string | undefined> => {
//   const user = await getCurrentUser();
//   if (user === undefined) {
//     return undefined;
//   }
//   return user.attributes.sub;
// };

// export const logoutUser = async (): Promise<void> => {
//   try {
//     await Auth.signOut();
//   } catch (error) {
//     console.error(error);
//   }
// };

export const registerUser = async (
  email: string,
  password: string,
  fullname: string,
  nickname: string,
  gender: string,
  birthdate: string
): Promise<AuthError | any> => {
  try {
    return await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          name: fullname,
          nickname,
          gender,
          birthdate
        }
      }
    });
  } catch (error) {
    return convertError(error);
  }
};

// export const loginUser = async (email: string, password: string): Promise<CognitoUser | AuthError | any> => {
//   try {
//     const user = await Auth.signIn(email, password);
//     return user;
//   } catch (error) {
//     return convertError(error);
//   }
// };

export const confirmSignUpCode = async (email: string, code: string): Promise<AuthError | any> => {
  try {
    await confirmSignUp({ username: email, confirmationCode: code });
    return {};
  } catch (error) {
    return convertError(error);
  }
};

export const requestPasswordReset = async (email: string): Promise<AuthError | any> => {
  try {
    await resetPassword({ username: email });
    return {};
  } catch (error) {
    return convertError(error);
  }
};

export const confirmPasswordReset = async (
  email: string,
  code: string,
  newPassword: string
): Promise<AuthError | any> => {
  try {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    return {};
  } catch (error) {
    return convertError(error);
  }
};

/**
 * Handles the complete new password challenge using signIn
 */
export const completeNewPasswordFlow = async (
  email: string,
  password: string,
  newPassword: string
): Promise<AuthError | any> => {
  try {
    // Step 1: Sign in to get the challenge
    const signInResult: any = await signIn({
      username: email,
      password
    });

    // Step 2: Check if user is in NEW_PASSWORD_REQUIRED state
    if (signInResult.challengeName === 'NEW_PASSWORD_REQUIRED') {
      // Step 3: Confirm the sign-in with the new password
      const confirmResult = await confirmSignIn({
        challengeResponse: newPassword
      });

      return confirmResult;
    } else {
      return {
        error: {
          code: 'IncompleteSignIn',
          message: 'Could not complete sign-in with the new password.'
        }
      };
    }
  } catch (error) {
    return convertError(error);
  }
};

export const updateUser = async (attributes: UserAttributes): Promise<AuthError | any> => {
  try {
    const stringifiedAttributes: Record<string, string> = Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [key, String(value)])
    );

    await updateUserAttributes({ userAttributes: stringifiedAttributes });
    return {};
  } catch (error) {
    return convertError(error);
  }
};

// export const deleteUserAccount = async (): Promise<void> => {
//   try {
//     const user: CognitoUser = await getCurrentUser();
//     if (user === undefined) {
//       // User not logged in
//       return;
//     }

//     // Deregister Push Notifications
//     await removeExternalUserId();

//     user.deleteUser((error) => {
//       if (error) {
//         console.error(error);
//       } else {
//         // Log user out from all devices
//         Auth.signOut({ global: true });
//       }
//     });
//   } catch (error) {
//     console.error(error);
//   }
// };
